import { Text, View, StyleSheet } from '@react-pdf/renderer';
import { sharedStyles, pdfColors } from './sharedStyles';
import RadioOptions from './RadioOptions';
import ContentRenderer from './ContentRenderer';
import type { AssessmentReportQuestionContentItem, AssessmentReportFormSection } from './types';

const styles = StyleSheet.create({
  formSlideDescription: {
    marginBottom: 12,
  },
  sectionContainer: {
    marginBottom: 12,
    paddingBottom: 12,
  },
  sectionTitle: {
    fontSize: 10,
    fontFamily: 'Inter-SemiBold',
    color: pdfColors.text,
    marginBottom: 6,
  },
  sectionDescription: {
    fontSize: 10,
    color: pdfColors.textLight,
    marginBottom: 8,
    lineHeight: 1.4,
  },
  fieldContainer: {
    marginBottom: 8,
    paddingLeft: 8,
  },
  lastFieldContainer: {
    marginTop: 10,
    marginBottom: 0,
  },
  fieldHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  fieldLabel: {
    fontSize: 9,
    fontFamily: 'Inter-SemiBold',
    color: pdfColors.text,
    flex: 1,
  },
  fieldBadge: {
    marginLeft: 8,
  },
  fieldHelperText: {
    fontSize: 8,
    color: pdfColors.textMuted,
    marginBottom: 4,
  },
  answerLabel: {
    fontSize: 8,
    fontFamily: 'Inter-SemiBold',
    color: pdfColors.textMuted,
    marginBottom: 2,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  badgeContainer: {
    flexDirection: 'row',
  },
  badge: {
    width: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  badgeLeft: {
    borderTopLeftRadius: 8,
    borderBottomLeftRadius: 8,
  },
  badgeRight: {
    borderTopRightRadius: 8,
    borderBottomRightRadius: 8,
  },
  badgeInactive: {
    backgroundColor: pdfColors.border,
    opacity: 0.5,
  },
  badgeCorrect: {
    backgroundColor: pdfColors.success,
  },
  badgeIncorrect: {
    backgroundColor: pdfColors.error,
  },
  badgeText: {
    fontSize: 10,
    fontFamily: 'Inter-SemiBold',
    color: pdfColors.white,
  },
  badgeTextInactiveIncorrect: {
    color: pdfColors.error,
  },
  badgeTextInactiveCorrect: {
    color: pdfColors.success,
  },
});

interface FormSlide {
  id: string;
  title: string;
  correct: boolean;
  description: AssessmentReportQuestionContentItem[];
  hint?: AssessmentReportQuestionContentItem[] | null;
  position: number;
  sections: AssessmentReportFormSection[];
}

interface FormSlideItemProps {
  formSlide: FormSlide;
  feedback?: string | null;
}

function FormSlideItem({ formSlide, feedback }: FormSlideItemProps): React.ReactElement {
  return (
    <View style={sharedStyles.itemContainer}>
      <View style={sharedStyles.itemHeader}>
        <Text style={sharedStyles.itemTitle}>
          {formSlide.position}. {formSlide.title}
        </Text>
        <View
          style={
            formSlide.correct ? sharedStyles.resultBadgeCorrect : sharedStyles.resultBadgeIncorrect
          }
        >
          <Text style={sharedStyles.resultBadgeText}>
            {formSlide.correct ? 'CORRECT' : 'INCORRECT'}
          </Text>
        </View>
      </View>

      {formSlide.description != null && formSlide.description.length > 0 ? (
        <View style={styles.formSlideDescription}>
          <ContentRenderer content={formSlide.description} />
        </View>
      ) : null}

      {formSlide.sections.map((section) => {
        return (
          <View key={section.id} style={styles.sectionContainer}>
            {section.title != null ? (
              <Text style={styles.sectionTitle}>{section.title}</Text>
            ) : null}

            {section.description != null && Array.isArray(section.description) ? (
              <View style={styles.sectionDescription}>
                <ContentRenderer content={section.description} />
              </View>
            ) : null}

            {section.fields.map((field) => {
              const userAnswer = field.userAnswer;

              return (
                <View key={field.id} style={styles.lastFieldContainer} wrap={false}>
                  <View style={styles.fieldHeader}>
                    <Text style={styles.fieldLabel}>
                      {field.label}
                      {field.required ? ' *' : ''}
                    </Text>
                    {userAnswer ? (
                      <View style={styles.badgeContainer}>
                        <View
                          style={[
                            styles.badge,
                            styles.badgeLeft,
                            userAnswer.correct ? styles.badgeInactive : styles.badgeIncorrect,
                          ]}
                        >
                          <Text
                            style={
                              userAnswer.correct
                                ? [styles.badgeText, styles.badgeTextInactiveIncorrect]
                                : styles.badgeText
                            }
                          >
                            ✗
                          </Text>
                        </View>
                        <View
                          style={[
                            styles.badge,
                            styles.badgeRight,
                            userAnswer.correct ? styles.badgeCorrect : styles.badgeInactive,
                          ]}
                        >
                          <Text
                            style={
                              !userAnswer.correct
                                ? [styles.badgeText, styles.badgeTextInactiveCorrect]
                                : styles.badgeText
                            }
                          >
                            ✓
                          </Text>
                        </View>
                      </View>
                    ) : null}
                  </View>

                  {field.helperText != null ? (
                    <Text style={styles.fieldHelperText}>{field.helperText}</Text>
                  ) : null}

                  {(field.fieldType === 'RADIO' || field.fieldType === 'SELECT') &&
                  field.options &&
                  field.options.length > 0 ? (
                    <RadioOptions
                      options={field.options.map((opt) => ({
                        id: opt.id,
                        label: opt.label,
                        isCorrect: opt.isCorrect ?? false,
                      }))}
                      selectedIds={
                        userAnswer?.selectedOptions
                          ? userAnswer.selectedOptions
                              .map((selectedValue) => {
                                const option = field.options?.find(
                                  (opt) => opt.value === selectedValue || opt.id === selectedValue,
                                );
                                return option?.id;
                              })
                              .filter((id): id is string => id != null)
                          : []
                      }
                    />
                  ) : userAnswer ? (
                    <View style={sharedStyles.answerSection}>
                      {userAnswer.textInputValue != null ? (
                        <Text style={sharedStyles.answerText}>{userAnswer.textInputValue}</Text>
                      ) : null}

                      {userAnswer.selectedOptions && userAnswer.selectedOptions.length > 0 ? (
                        <Text style={sharedStyles.answerText}>
                          {userAnswer.selectedOptions
                            .map((optionId) => {
                              const option = field.options?.find((opt) => opt.id === optionId);
                              return option?.label ?? optionId;
                            })
                            .join(', ')}
                        </Text>
                      ) : null}

                      {userAnswer.textInputValue == null &&
                      (!userAnswer.selectedOptions || userAnswer.selectedOptions.length === 0) ? (
                        <Text style={sharedStyles.answerText}>No answer provided</Text>
                      ) : null}
                    </View>
                  ) : (
                    <View style={sharedStyles.answerSection}>
                      <Text style={sharedStyles.answerText}>No answer provided</Text>
                    </View>
                  )}
                </View>
              );
            })}
          </View>
        );
      })}

      {feedback != null && feedback.length > 0 ? (
        <View style={sharedStyles.feedbackSection} wrap={false}>
          <Text style={sharedStyles.feedbackLabel}>ASSESSOR FEEDBACK</Text>
          <Text style={sharedStyles.feedbackText}>{feedback}</Text>
        </View>
      ) : null}
    </View>
  );
}

export default FormSlideItem;
